import { PAGE_CODES } from 'constants/pageCodes';
import { lazyWithRetry } from 'utils/chunkLoadRecovery';
import { EnhancedPath } from 'utils/history';

export const COMPANY_ROUTING_PARAMS = {
  userId: 'userId',
  tabName: 'tabName',
  companyId: 'companyId',
};
export const APPLY_FORM_CUSTOM_QUESTION_PARAMS = {
  customQuestionId: 'customQuestionId',
};

export const SNIPPET_PARAMS = {
  snippetId: 'snippetId',
};

export const POS_CONFIGURATION_PARAMS = {
  configId: 'configId',
  section: 'section',
};

export const TPO_SIGNUP_CONFIGURATION_PARAMS = {
  configId: 'configId',
  section: 'section',
};

export const TPO_DENIAL_CONFIGURATION_PARAMS = {
  categoryId: 'categoryId',
  reasonId: 'reasonId',
};

export const ADVISOR_PORTAL_CONFIGURATION_PARAMS = {
  configId: 'configId',
  section: 'section',
};

export class CompanyPaths {
  static company = new EnhancedPath('/company');
  static companyAudioRecordings = new EnhancedPath('/company-audio-recordings');
  static applyFormCustomQuestions = new EnhancedPath('/apply-form-custom-questions');
  static applyFormCustomQuestion = new EnhancedPath(
    `${CompanyPaths.applyFormCustomQuestions.path}/:${APPLY_FORM_CUSTOM_QUESTION_PARAMS.customQuestionId}?`,
  );
  static loanOfficersManagement = new EnhancedPath('/loan-officers-management');
  static leadSources = new EnhancedPath('/lead-sources');
  static accountingSettings = new EnhancedPath('/accounting-settings');
  static csvMappings = new EnhancedPath('/csv-mappings');
  static posConfigurations = new EnhancedPath('/pos-configurations');
  static posConfiguration = new EnhancedPath(
    `${CompanyPaths.posConfigurations.path}/:${POS_CONFIGURATION_PARAMS.configId}/:${POS_CONFIGURATION_PARAMS.section}?`,
  );
  static tpoSignupConfigurations = new EnhancedPath('/tpo-signup-configurations');
  static tpoSignupConfiguration = new EnhancedPath(
    `${CompanyPaths.tpoSignupConfigurations.path}/:${TPO_SIGNUP_CONFIGURATION_PARAMS.configId}/:${TPO_SIGNUP_CONFIGURATION_PARAMS.section}?`,
  );
  static tpoStatusConfiguration = new EnhancedPath('/tpo-status-configuration');
  static tpoDenialConfiguration = new EnhancedPath(
    `/tpo-denial-configuration/:${TPO_DENIAL_CONFIGURATION_PARAMS.categoryId}?/:${TPO_DENIAL_CONFIGURATION_PARAMS.reasonId}?`,
  );
  static advisorPortalConfigurations = new EnhancedPath('/advisor-portal-configurations');
  static advisorPortalConfiguration = new EnhancedPath(
    `${CompanyPaths.advisorPortalConfigurations.path}/:${ADVISOR_PORTAL_CONFIGURATION_PARAMS.configId}/:${ADVISOR_PORTAL_CONFIGURATION_PARAMS.section}?`,
  );

  static companyUserTab = new EnhancedPath(
    `${CompanyPaths.company.path}/users/:${COMPANY_ROUTING_PARAMS.userId}/:${COMPANY_ROUTING_PARAMS.tabName}?`,
  );

  static companyTab = new EnhancedPath(
    `${CompanyPaths.company.path}/:${COMPANY_ROUTING_PARAMS.tabName}?`,
  );
  static emailSignaturePreview = new EnhancedPath(
    `/email-signature-preview/:${COMPANY_ROUTING_PARAMS.companyId}?`,
  );

  static emailSnippetPreview = new EnhancedPath(
    `/email-snippet-preview/:${SNIPPET_PARAMS.snippetId}?`,
  );
}

const routesConfig = {
  settings: {
    layout: {
      config: {},
    },
  },
  routes: [
    {
      path: CompanyPaths.companyUserTab.path,
      component: lazyWithRetry(() => import('app/pages/system/users/user')),
    },
    {
      path: CompanyPaths.companyTab.path,
      component: lazyWithRetry(() => import('app/pages/companies/companies/company')),
      pageCode: PAGE_CODES.COMPANY,
    },
    {
      path: CompanyPaths.leadSources.path,
      component: lazyWithRetry(() => import('app/pages/companies/leadSources')),
      pageCode: PAGE_CODES.LEAD_SOURCES,
    },
    {
      path: CompanyPaths.accountingSettings.path,
      component: lazyWithRetry(() => import('app/pages/companies/accountingSettings')),
      pageCode: PAGE_CODES.ACCOUNTING_SETTINGS,
    },
    {
      path: CompanyPaths.applyFormCustomQuestion.path,
      component: lazyWithRetry(() => import('app/pages/companies/applyFormCustomQuestions')),
      pageCode: PAGE_CODES.APPLY_FORM_CUSTOM_QUESTIONS,
    },
    {
      path: CompanyPaths.csvMappings.path,
      component: lazyWithRetry(() => import('app/pages/companies/csvMappings')),
      pageCode: PAGE_CODES.CSV_MAPPINGS,
    },
    {
      path: CompanyPaths.companyAudioRecordings.path,
      component: lazyWithRetry(() => import('app/pages/companies/audioRecordings')),
      pageCode: PAGE_CODES.COMPANY_AUDIO_RECORDINGS,
    },
    {
      path: CompanyPaths.posConfiguration.path,
      component: lazyWithRetry(() => import('app/pages/companies/posConfigurations')),
      pageCode: PAGE_CODES.POS_CONFIGURATIONS,
    },
    {
      path: CompanyPaths.posConfigurations.path,
      component: lazyWithRetry(() => import('app/pages/companies/posConfigurations')),
      pageCode: PAGE_CODES.POS_CONFIGURATIONS,
    },
    {
      path: CompanyPaths.tpoSignupConfiguration.path,
      component: lazyWithRetry(() => import('app/pages/companies/tpoSignupConfigurations')),
      pageCode: PAGE_CODES.TPO_SIGNUP_CONFIGURATIONS,
    },
    {
      path: CompanyPaths.tpoSignupConfigurations.path,
      component: lazyWithRetry(() => import('app/pages/companies/tpoSignupConfigurations')),
      pageCode: PAGE_CODES.TPO_SIGNUP_CONFIGURATIONS,
    },
    {
      path: CompanyPaths.tpoStatusConfiguration.path,
      component: lazyWithRetry(() => import('app/pages/companies/tpoStatusConfiguration')),
      pageCode: PAGE_CODES.TPO_SIGNUP_CONFIGURATIONS,
    },
    {
      path: CompanyPaths.tpoDenialConfiguration.path,
      component: lazyWithRetry(() => import('app/pages/companies/tpoDenialConfiguration')),
      pageCode: PAGE_CODES.TPO_SIGNUP_CONFIGURATIONS,
    },
    {
      path: CompanyPaths.advisorPortalConfiguration.path,
      component: lazyWithRetry(() => import('app/pages/companies/advisorPortalConfigurations')),
      pageCode: PAGE_CODES.ADVISOR_PORTAL_CONFIGURATIONS,
    },
    {
      path: CompanyPaths.advisorPortalConfigurations.path,
      component: lazyWithRetry(() => import('app/pages/companies/advisorPortalConfigurations')),
      pageCode: PAGE_CODES.ADVISOR_PORTAL_CONFIGURATIONS,
    },
    {
      path: CompanyPaths.loanOfficersManagement.path,
      component: lazyWithRetry(() => import('app/pages/companies/loanOfficersManagement')),
      pageCode: PAGE_CODES.LOAN_OFFICERS_MANAGEMENT,
    },
    {
      path: CompanyPaths.emailSignaturePreview.path,
      component: lazyWithRetry(
        () =>
          import('app/pages/companies/companies/company/tabs/emailSignature/EmailSignaturePreview'),
      ),
      pageCode: PAGE_CODES.EMAIL_SIGNATURE_TEMPLATE,
    },
    {
      path: CompanyPaths.emailSnippetPreview.path,
      component: lazyWithRetry(
        () =>
          import('app/pages/companies/companies/company/tabs/emailSnippets/EmailSnippetPreview'),
      ),
      pageCode: PAGE_CODES.EMAIL_SNIPPETS_TEMPLATE,
    },
  ],
};

export default routesConfig;
